import React, { useCallback, useEffect, useState } from "react";
import { Link, Routes, Route, useNavigate, useParams } from "react-router-dom";
import "../academy/academy.scss";
import "./staff.scss";
import AcademyLessonContent from "../academy/AcademyLessonContent";
import {
  getLessonProgress,
  OPTION_LETTERS,
  scrollTrainingToTop,
  TrainingFeedback,
  TrainingLessonList,
  TrainingLoading,
  TrainingProgressBar,
  TrainingStickyActions,
  TrainingSteps,
  TrainingTitleRow,
  lessonTypeLabel,
  formatLessonDuration,
  sumLessonDurations,
} from "../academy/AcademyShared";
import {
  getStaffToken,
  getStaffProfile,
  clearStaffSession,
  requestStaffOtp,
  validateStaffOtp,
  redeemStaffHandoff,
  getStaffAcademyMe,
  getStaffAcademyModule,
  postStaffAcademyProgress,
  getStaffAcademyQuiz,
  submitStaffAcademyQuiz,
} from "../../services/staffApi";
import CareersFooter from "../../components/CareersFooter";

function stripLeadingTitle(markdown, title) {
  if (!markdown || !title) return markdown || "";
  const escaped = title.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  return markdown
    .replace(new RegExp(`^\\s*#\\s*${escaped}\\s*\\n+`, "i"), "")
    .trimStart();
}

function StaffShell({ children, title, onSignOut, profile, sticky = false }) {
  return (
    <div className="training-center">
      <div className="training-center__bg" aria-hidden="true" />
      <header className="training-header">
        <div className="training-header-title-wrap">
          <Link to="/staff" className="training-header-brand">
            QikTruck <span>Staff</span>
          </Link>
          <h1 className="training-header-title">
            <span className="training-header-title__text">{title}</span>
          </h1>
        </div>
        {profile || onSignOut ? (
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            {profile?.firstname || profile?.email ? (
              <span style={{ fontSize: 14, opacity: 0.85 }}>
                {[profile.firstname, profile.lastname].filter(Boolean).join(" ") ||
                  profile.email}
              </span>
            ) : null}
            {onSignOut ? (
              <button type="button" className="training-btn-secondary" onClick={onSignOut}>
                Sign out
              </button>
            ) : null}
          </div>
        ) : null}
      </header>
      <div className="training-shell">
        <main className={`training-body${sticky ? " has-sticky" : ""}`}>{children}</main>
      </div>
      <CareersFooter />
    </div>
  );
}

function StaffLogin({ onLoggedIn }) {
  const [idnumber, setIdnumber] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const sendOtp = async (e) => {
    e?.preventDefault?.();
    if (!idnumber.trim()) return;
    setLoading(true);
    setError("");
    try {
      await requestStaffOtp(idnumber);
      setOtpSent(true);
    } catch (err) {
      setError(
        err.message ||
          "Could not send a sign-in code. Check your work email or ID number."
      );
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async (e) => {
    e.preventDefault();
    if (!idnumber.trim() || !otp.trim()) return;
    setLoading(true);
    setError("");
    try {
      await validateStaffOtp(idnumber, otp);
      onLoggedIn();
    } catch (err) {
      setError(err.message || "Invalid code. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="training-card" style={{ maxWidth: 420, margin: "0 auto", padding: 24 }}>
      <h2 style={{ margin: "0 0 8px", fontSize: 20 }}>Sign in</h2>
      <p style={{ margin: "0 0 20px", opacity: 0.8, fontSize: 14, lineHeight: 1.45 }}>
        Use the same work email or ID number you use for HQ. We will send a one-time
        code to your phone.
      </p>

      {!otpSent ? (
        <form onSubmit={sendOtp}>
          <label htmlFor="staff-id" style={{ display: "block", marginBottom: 8, fontWeight: 600 }}>
            Work email or ID number
          </label>
          <input
            id="staff-id"
            type="text"
            autoComplete="username"
            value={idnumber}
            onChange={(e) => setIdnumber(e.target.value)}
            placeholder="e.g. you@qiktruck.co.za"
            style={{ width: "100%", marginBottom: 16, padding: "10px 12px" }}
          />
          <button
            type="submit"
            className="training-btn-primary"
            disabled={loading || !idnumber.trim()}
            style={{ width: "100%" }}
          >
            {loading ? "Sending code…" : "Send sign-in code"}
          </button>
        </form>
      ) : (
        <form onSubmit={verifyOtp}>
          <p style={{ margin: "0 0 12px", fontSize: 14 }}>
            Code sent for <strong>{idnumber.trim()}</strong>.{" "}
            <button
              type="button"
              onClick={() => {
                setOtpSent(false);
                setOtp("");
                setError("");
              }}
              style={{
                background: "none",
                border: "none",
                color: "#f5861f",
                cursor: "pointer",
                padding: 0,
                textDecoration: "underline",
              }}
            >
              Change
            </button>
          </p>
          <label htmlFor="staff-otp" style={{ display: "block", marginBottom: 8, fontWeight: 600 }}>
            Sign-in code
          </label>
          <input
            id="staff-otp"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            value={otp}
            onChange={(e) => setOtp(e.target.value)}
            placeholder="Enter the code from SMS"
            style={{ width: "100%", marginBottom: 16, padding: "10px 12px" }}
          />
          <button
            type="submit"
            className="training-btn-primary"
            disabled={loading || !otp.trim()}
            style={{ width: "100%", marginBottom: 8 }}
          >
            {loading ? "Signing in…" : "Sign in"}
          </button>
          <button
            type="button"
            className="training-btn-secondary"
            disabled={loading}
            onClick={sendOtp}
            style={{ width: "100%" }}
          >
            Resend code
          </button>
        </form>
      )}

      {error ? (
        <p style={{ color: "crimson", marginTop: 16, fontSize: 14 }}>{error}</p>
      ) : null}
    </div>
  );
}

function humanStatus(status) {
  const map = {
    not_started: "Not started",
    in_progress: "In progress",
    completed: "Completed",
    assigned: "Assigned",
    cancelled: "Cancelled",
  };
  return map[status] || String(status || "Not started").replace(/_/g, " ");
}

function quizPassRequirementLabel(passPct) {
  const pct = passPct ?? 100;
  if (pct >= 100) return "Score 100% (every question correct) to complete this module.";
  return `Score ${pct}% or higher to complete this module.`;
}

function StaffHome() {
  const [signedIn, setSignedIn] = useState(Boolean(getStaffToken()));
  const [profile, setProfile] = useState(getStaffProfile());
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [handoffBusy, setHandoffBusy] = useState(false);

  const load = useCallback(async () => {
    if (!getStaffToken()) {
      setData(null);
      setSignedIn(false);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const me = await getStaffAcademyMe();
      setData(me);
      setSignedIn(true);
      setProfile(getStaffProfile());
    } catch (e) {
      if (/unauthor/i.test(e.message || "") || /forbidden/i.test(e.message || "")) {
        clearStaffSession();
        setSignedIn(false);
        setData(null);
        setError("Session expired. Sign in again.");
      } else {
        setError(e.message || "Failed to load assignments");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const handoff = params.get("handoff");
    if (!handoff) {
      load();
      return;
    }
    let cancelled = false;
    (async () => {
      setHandoffBusy(true);
      setError("");
      try {
        await redeemStaffHandoff(handoff);
        params.delete("handoff");
        const next = `${window.location.pathname}${
          params.toString() ? `?${params}` : ""
        }${window.location.hash || ""}`;
        window.history.replaceState({}, "", next);
        if (!cancelled) {
          setSignedIn(true);
          setProfile(getStaffProfile());
          await load();
        }
      } catch (e) {
        if (!cancelled) {
          setError(
            e.message ||
              "Could not open staff training from HQ. Sign in with OTP, or try Open on careers site again."
          );
          setSignedIn(Boolean(getStaffToken()));
        }
      } finally {
        if (!cancelled) setHandoffBusy(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [load]);

  const signOut = () => {
    clearStaffSession();
    setSignedIn(false);
    setProfile(null);
    setData(null);
    setError("");
  };

  return (
    <StaffShell
      title="Staff training"
      profile={signedIn ? profile : null}
      onSignOut={signedIn ? signOut : null}
    >
      {handoffBusy ? (
        <TrainingLoading label="Opening your HQ session…" />
      ) : null}
      {!handoffBusy && !signedIn ? (
        <>
          <section className="training-hero" style={{ marginBottom: 24, textAlign: "center" }}>
            <div className="training-kicker">HQ staff</div>
            <TrainingTitleRow typeChip="module" title="Staff training" />
            <p className="training-subtitle">
              Your assigned QikTruck staff modules. Sign in to continue learning.
            </p>
          </section>
          {error ? <TrainingFeedback feedback={{ type: "error", message: error }} /> : null}
          <StaffLogin
            onLoggedIn={() => {
              setProfile(getStaffProfile());
              setSignedIn(true);
              setError("");
              load();
            }}
          />
        </>
      ) : !handoffBusy ? (
        <>
          <section className="training-hero" style={{ marginBottom: 24 }}>
            <div className="training-kicker">Your assignments</div>
            <TrainingTitleRow typeChip="module" title="Staff modules" />
            <p className="training-subtitle">
              Open a module, work through each lesson, then take the final quiz.
            </p>
          </section>

          {error ? <TrainingFeedback feedback={{ type: "error", message: error }} /> : null}
          {loading ? <TrainingLoading label="Loading assignments…" /> : null}

          {!loading && data?.assignments?.length === 0 ? (
            <div className="training-empty">
              <p>No modules assigned to your account yet. Ask your manager if you expected one.</p>
            </div>
          ) : null}

          <div className="staff-assignment-list">
            {(data?.assignments || []).map((a) => {
              const pct = Math.min(100, parseInt(a.watch_pct, 10) || 0);
              const progressLabel = humanStatus(a.progress_status);
              const done = a.progress_status === "completed";
              return (
                <article
                  key={a.id}
                  className={`staff-assignment-card${done ? " is-done" : ""}${
                    pct > 0 && !done ? " is-active" : ""
                  }`}
                >
                  <div className="staff-assignment-card__top">
                    <div className="staff-assignment-card__copy">
                      <span className="staff-assignment-card__badge">
                        {humanStatus(a.status)}
                      </span>
                      <h2 className="staff-assignment-card__title">{a.module_title}</h2>
                      {a.module_description ? (
                        <p className="staff-assignment-card__desc">
                          {a.module_description.split("\n\n")[0]}
                        </p>
                      ) : null}
                    </div>
                    <Link
                      className="training-btn-primary staff-assignment-card__cta"
                      to={`/staff/modules/${a.module_slug}`}
                    >
                      {done ? "Review" : pct > 0 ? "Continue" : "Start"}
                    </Link>
                  </div>
                  <div className="staff-assignment-card__meta">
                    {[
                      progressLabel,
                      a.quiz_best_score != null ? `Quiz best ${a.quiz_best_score}%` : null,
                      `${pct}% lessons`,
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </div>
                  <TrainingProgressBar
                    value={pct}
                    label={`${pct}% of lessons complete`}
                  />
                </article>
              );
            })}
          </div>
        </>
      ) : null}
    </StaffShell>
  );
}

function StaffModule() {
  const { moduleSlug } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [activeLesson, setActiveLesson] = useState(null);
  const [error, setError] = useState("");
  const [completing, setCompleting] = useState(false);
  const [quizLoading, setQuizLoading] = useState(false);
  const [quizQuestions, setQuizQuestions] = useState([]);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizResult, setQuizResult] = useState(null);
  const [showQuiz, setShowQuiz] = useState(false);
  const [questionIdx, setQuestionIdx] = useState(0);
  const [feedback, setFeedback] = useState(null);
  const profile = getStaffProfile();

  const load = useCallback(async () => {
    if (!getStaffToken()) {
      navigate("/staff");
      return null;
    }
    const res = await getStaffAcademyModule(moduleSlug);
    setData(res);
    return res;
  }, [moduleSlug, navigate]);

  useEffect(() => {
    setActiveLesson(null);
    setShowQuiz(false);
    setQuizResult(null);
    setFeedback(null);
    load().catch((e) => setError(e.message));
    scrollTrainingToTop({ instant: true });
  }, [load]);

  useEffect(() => {
    if (!feedback) return undefined;
    const timer = setTimeout(() => setFeedback(null), 5000);
    return () => clearTimeout(timer);
  }, [feedback]);

  const lessons = data?.lessons || [];
  const { pct, done, total } = getLessonProgress(lessons);
  const moduleDurationLabel = formatLessonDuration(sumLessonDurations(lessons));
  const nextLesson = lessons.find((l) => l.status !== "completed");
  const steps = [...lessons.map((l) => (l.title || "").slice(0, 14)), "Quiz"];
  const quizStepDone =
    (data?.progress?.quiz_best_score ?? 0) >= (data?.quiz?.pass_score_pct ?? 100);
  const stepsCompleted = [...lessons.map((l) => l.status === "completed"), quizStepDone];
  const currentStep = activeLesson
    ? lessons.findIndex((l) => l.id === activeLesson.id)
    : showQuiz
      ? lessons.length
      : -1;
  const allQuizAnswered =
    quizQuestions.length > 0 && quizQuestions.every((qq) => quizAnswers[qq.id]);

  const openLesson = (lesson, lessonList = lessons) => {
    const fresh = lessonList.find((l) => l.id === lesson.id) || lesson;
    setActiveLesson(fresh);
    setShowQuiz(false);
    setQuizResult(null);
    setFeedback(null);
    scrollTrainingToTop({ instant: true });
  };

  const completeLesson = async () => {
    if (!activeLesson || completing) return;
    setCompleting(true);
    setError("");
    setFeedback(null);
    try {
      await postStaffAcademyProgress({
        lesson_id: activeLesson.id,
        watch_pct: 100,
        time_spent_sec: 60,
        lesson_complete: true,
      });
      const refreshed = await load();
      const nextLessons = refreshed?.lessons || [];
      setFeedback({ type: "success", message: "Lesson complete. Moving on…" });
      const idx = nextLessons.findIndex((l) => l.id === activeLesson.id);
      const next = nextLessons[idx + 1];
      if (next) {
        openLesson(next, nextLessons);
      } else if (refreshed?.quiz) {
        setActiveLesson(null);
        await startQuiz(refreshed);
      } else {
        setActiveLesson(null);
        scrollTrainingToTop({ instant: true });
      }
    } catch (e) {
      setFeedback({ type: "error", message: e.message || "Could not save progress" });
    } finally {
      setCompleting(false);
    }
  };

  const startQuiz = async (moduleData = data) => {
    if (!moduleData?.quiz) return;
    setQuizLoading(true);
    setFeedback(null);
    try {
      const res = await getStaffAcademyQuiz(moduleData.quiz.id);
      setQuizQuestions(res.questions || []);
      setQuizAnswers({});
      setQuestionIdx(0);
      setQuizResult(null);
      setShowQuiz(true);
      setActiveLesson(null);
      scrollTrainingToTop({ instant: true });
    } catch (e) {
      setFeedback({ type: "error", message: e.message });
    } finally {
      setQuizLoading(false);
    }
  };

  const submitQuiz = async () => {
    if (!data?.quiz || !allQuizAnswered || completing) return;
    setCompleting(true);
    setFeedback(null);
    try {
      const res = await submitStaffAcademyQuiz(data.quiz.id, quizAnswers);
      setQuizResult(res);
      setShowQuiz(false);
      setFeedback({
        type: res.passed ? "success" : "error",
        message: res.passed
          ? `Passed with ${res.score_pct}%.`
          : `Scored ${res.score_pct}%. Need ${data.quiz.pass_score_pct}% to pass.`,
      });
      await load();
      scrollTrainingToTop({ instant: true });
    } catch (e) {
      setFeedback({ type: "error", message: e.message });
    } finally {
      setCompleting(false);
    }
  };

  const signOut = () => {
    clearStaffSession();
    navigate("/staff");
  };

  const sticky = Boolean(activeLesson || (showQuiz && !quizResult));

  if (!data && !error) {
    return (
      <StaffShell title="Loading…" profile={profile} onSignOut={signOut}>
        <TrainingLoading label="Loading module…" />
      </StaffShell>
    );
  }

  if (error && !data) {
    return (
      <StaffShell title="Module" profile={profile} onSignOut={signOut}>
        <TrainingFeedback feedback={{ type: "error", message: error }} />
        <Link to="/staff" className="training-btn-secondary">
          Back to assignments
        </Link>
      </StaffShell>
    );
  }

  if (!activeLesson && !showQuiz && !quizResult) {
    return (
      <StaffShell
        title={data.module.title}
        profile={profile}
        onSignOut={signOut}
      >
        <p style={{ marginBottom: 16 }}>
          <Link to="/staff">Back to assignments</Link>
        </p>

        <div className="training-hook">
          <div>
            <div className="training-kicker">Staff module</div>
            <TrainingTitleRow
              typeChip="module"
              title={data.module.title}
              className="training-title-row--module-hook"
            />
          </div>
        </div>

        {data.module.description ? (
          <div className="training-reading-card staff-module-overview">
            <AcademyLessonContent markdown={data.module.description} />
          </div>
        ) : null}

        <TrainingProgressBar
          value={pct}
          label={`${done} of ${total} lessons complete`}
        />

        <h3 className="training-section-title">
          Lessons
          {moduleDurationLabel ? (
            <span className="training-section-meta">About {moduleDurationLabel} total</span>
          ) : null}
        </h3>

        <TrainingLessonList
          lessons={lessons}
          onSelect={openLesson}
          nextLessonId={nextLesson?.id}
        />

        {data.quiz ? (
          <div className="training-card" style={{ marginTop: 16 }}>
            <h3 className="training-program-title">Final quiz</h3>
            <p className="training-subtitle" style={{ fontSize: 15, marginBottom: 12 }}>
              {quizStepDone ? (
                <>
                  Quiz passed
                  {data.progress?.quiz_best_score != null
                    ? ` with ${data.progress.quiz_best_score}%.`
                    : "."}{" "}
                  You can retake to practise, but the module is already complete.
                </>
              ) : (
                <>
                  {quizPassRequirementLabel(data.quiz.pass_score_pct)}
                  {data.progress?.quiz_best_score != null
                    ? ` Your best score so far is ${data.progress.quiz_best_score}%.`
                    : ""}
                </>
              )}
            </p>
            <button
              type="button"
              className="training-btn-primary"
              onClick={() => startQuiz()}
              disabled={quizLoading || (lessons.length > 0 && done < lessons.length)}
            >
              {quizLoading
                ? "Loading quiz…"
                : done < lessons.length
                  ? "Complete all lessons first"
                  : quizStepDone
                    ? "Retake quiz"
                    : data.progress?.quiz_best_score != null
                      ? "Retry quiz"
                      : "Take final quiz"}
            </button>
          </div>
        ) : null}

        <TrainingFeedback feedback={feedback} />
      </StaffShell>
    );
  }

  if (showQuiz && !quizResult) {
    const q = quizQuestions[questionIdx];
    return (
      <StaffShell
        title="Final quiz"
        profile={profile}
        onSignOut={signOut}
        sticky
      >
        <TrainingSteps steps={steps} current={lessons.length} completed={stepsCompleted} />
        {quizLoading || !q ? (
          <TrainingLoading label="Loading quiz…" />
        ) : (
          <>
            <div className="training-quiz-header">
              <div className="training-kicker">
                Question {questionIdx + 1} of {quizQuestions.length}
              </div>
              <h2 className="training-quiz-question">{q.question_text}</h2>
            </div>
            <div className="training-quiz-options training-quiz-options--grid">
              {(q.options || []).map((opt, i) => (
                <button
                  key={`${q.id}-${i}`}
                  type="button"
                  className={`training-quiz-option ${
                    quizAnswers[q.id] === opt ? "selected" : ""
                  }`}
                  onClick={() => setQuizAnswers({ ...quizAnswers, [q.id]: opt })}
                >
                  <span className="training-quiz-option-letter">{OPTION_LETTERS[i]}</span>
                  {opt}
                </button>
              ))}
            </div>
            <TrainingFeedback feedback={feedback} />
            <TrainingStickyActions>
              <div className="training-sticky-inner training-quiz-nav">
                {questionIdx > 0 ? (
                  <button
                    type="button"
                    className="training-btn-secondary"
                    onClick={() => setQuestionIdx(questionIdx - 1)}
                  >
                    Back
                  </button>
                ) : (
                  <button
                    type="button"
                    className="training-btn-secondary"
                    onClick={() => setShowQuiz(false)}
                  >
                    Exit quiz
                  </button>
                )}
                {questionIdx < quizQuestions.length - 1 ? (
                  <button
                    type="button"
                    className="training-btn-primary"
                    disabled={!quizAnswers[q.id]}
                    onClick={() => setQuestionIdx(questionIdx + 1)}
                  >
                    Next
                  </button>
                ) : (
                  <button
                    type="button"
                    className="training-btn-primary"
                    disabled={!allQuizAnswered || completing}
                    onClick={submitQuiz}
                  >
                    {completing
                      ? "Submitting…"
                      : !allQuizAnswered
                        ? "Answer all questions"
                        : "Submit quiz"}
                  </button>
                )}
              </div>
            </TrainingStickyActions>
          </>
        )}
      </StaffShell>
    );
  }

  if (quizResult) {
    return (
      <StaffShell title="Results" profile={profile} onSignOut={signOut}>
        <div className="training-result-panel">
          <div
            className={`training-result-card ${
              quizResult.passed
                ? "training-result-card--pass"
                : "training-result-card--fail"
            }`}
          >
            <div className="training-kicker">
              {quizResult.passed ? "Passed" : "Try again"}
            </div>
            <div className="training-result-score">{quizResult.score_pct}%</div>
            <p className="training-subtitle" style={{ marginBottom: 0 }}>
              {quizResult.passed
                ? "Module complete. Nice work."
                : `You need ${
                    data.quiz?.pass_score_pct >= 100
                      ? "100% (every question correct)"
                      : `${data.quiz?.pass_score_pct || 100}%`
                  } to pass. Review the lessons and try again.`}
            </p>
          </div>
          <div className="training-result-actions">
            {!quizResult.passed ? (
              <button
                type="button"
                className="training-btn-secondary"
                onClick={() => startQuiz()}
              >
                Retry quiz
              </button>
            ) : null}
            <button
              type="button"
              className="training-btn-primary"
              onClick={() => {
                setQuizResult(null);
                setShowQuiz(false);
                setActiveLesson(null);
              }}
            >
              Back to module
            </button>
          </div>
        </div>
        <TrainingFeedback feedback={feedback} />
      </StaffShell>
    );
  }

  const lessonIndex = lessons.findIndex((l) => l.id === activeLesson.id);
  const hasNext = lessonIndex >= 0 && lessonIndex < lessons.length - 1;

  return (
    <StaffShell
      title={data?.module?.title || activeLesson.title}
      profile={profile}
      onSignOut={signOut}
      sticky={sticky}
    >
      <TrainingSteps steps={steps} current={currentStep} completed={stepsCompleted} />

      <div className="training-reading-label">
        Lesson {lessonIndex + 1} of {lessons.length}
        {" · "}
        {lessonTypeLabel(activeLesson.lesson_type)}
        {formatLessonDuration(activeLesson.duration_sec) ? (
          <>
            {" · "}
            <span className="training-lesson-duration-inline">
              ~{formatLessonDuration(activeLesson.duration_sec)}
            </span>
          </>
        ) : null}
      </div>

      <TrainingTitleRow typeChip="lesson" title={activeLesson.title} />

      <div className="training-reading-card">
        <AcademyLessonContent
          key={activeLesson.id}
          markdown={stripLeadingTitle(activeLesson.content_text || "", activeLesson.title)}
        />
      </div>

      <TrainingFeedback feedback={feedback} />

      <TrainingStickyActions>
        <div className="training-sticky-inner training-lesson-nav">
          <button
            type="button"
            className="training-btn-secondary"
            disabled={completing}
            onClick={() => {
              setActiveLesson(null);
              scrollTrainingToTop({ instant: true });
            }}
          >
            Lesson list
          </button>
          {activeLesson.status === "completed" ? (
            <button
              type="button"
              className="training-btn-primary"
              onClick={() => {
                if (hasNext) openLesson(lessons[lessonIndex + 1]);
                else {
                  setActiveLesson(null);
                  scrollTrainingToTop({ instant: true });
                }
              }}
            >
              {hasNext ? "Next lesson" : "Back to module"}
            </button>
          ) : (
            <button
              type="button"
              className="training-btn-primary"
              disabled={completing}
              onClick={completeLesson}
            >
              {completing
                ? "Saving…"
                : hasNext
                  ? "Mark complete & continue"
                  : "Mark complete"}
            </button>
          )}
        </div>
      </TrainingStickyActions>
    </StaffShell>
  );
}

export default function StaffCenter() {
  return (
    <Routes>
      <Route index element={<StaffHome />} />
      <Route path="modules/:moduleSlug" element={<StaffModule />} />
    </Routes>
  );
}
